import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { isActive } from '../../config/constants/liquidityFilterData';
import type { Filters } from '../../config/constants/liquidityFilterData';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';
import { useFilteredSecurities } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/useFilteredSecurities';

export function useLiquidityFilters() {
  const { distributions, filterExcludedCounts, dataRanges } =
    useFilteredSecurities();

  const { liquidity, size, history, setLiquidity, setSize, setHistory } =
    useSmartBetaStrategyStore(
      useShallow((state) => ({
        liquidity: state.data_processor.liquidity,
        size: state.data_processor.size,
        history: state.data_processor.history,
        setLiquidity: state.setLiquidity,
        setSize: state.setSize,
        setHistory: state.setHistory,
      })),
    );

  const filters: Filters = useMemo(
    () => ({
      price: [liquidity.price_range.min, liquidity.price_range.max],
      adv: [liquidity.avg_dollar_volume.min, liquidity.avg_dollar_volume.max],
      float: [liquidity.float.min, liquidity.float.max],
      marketCap: [size.min, size.max],
      history: [history.min, history.max],
    }),
    [liquidity, size, history],
  );

  const update = useCallback(
    <TKey extends keyof Filters>(key: TKey, value: Filters[TKey]) => {
      const [min, max] = value;
      switch (key) {
        case 'price':
          setLiquidity({ price_range: { min, max } });
          break;
        case 'adv':
          setLiquidity({ avg_dollar_volume: { min, max } });
          break;
        case 'float':
          setLiquidity({ float: { min, max } });
          break;
        case 'marketCap':
          setSize({ min, max });
          break;
        case 'history':
          setHistory({ min, max });
          break;
      }
    },
    [setLiquidity, setSize, setHistory],
  );

  const liquidityActive = useMemo(() => {
    let c = 0;
    if (isActive(filters.price, dataRanges.price)) c++;
    if (isActive(filters.adv, dataRanges.adv)) c++;
    if (isActive(filters.float, dataRanges.float)) c++;
    return c;
  }, [filters.price, filters.adv, filters.float, dataRanges]);

  const sizeActive = useMemo(() => {
    return isActive(filters.marketCap, dataRanges.marketCap) ? 1 : 0;
  }, [filters.marketCap, dataRanges]);

  const historyActive = useMemo(() => {
    return isActive(filters.history, dataRanges.history) ? 1 : 0;
  }, [filters.history, dataRanges]);

  const totalActive = liquidityActive + sizeActive + historyActive;

  return {
    filters,
    update,
    liquidityActive,
    sizeActive,
    historyActive,
    totalActive,
    distributions,
    filterExcludedCounts,
  };
}
